import { useEffect, useRef, useState } from 'react';
import { readOnline } from '../utils/connectivity.js';

const currentNavigator = () => (typeof navigator === 'undefined' ? null : navigator);

/**
 * État du réseau annoncé par le navigateur (UI-11) : événements online / offline, valeur
 * initiale navigator.onLine (absente : en ligne). onReconnect est appelé à chaque retour
 * du réseau après une coupure observée (relance des enregistrements, relecture des listes).
 * @param {{onReconnect?: () => void}} options
 * @returns {boolean} en ligne
 */
export function useOnline({ onReconnect = null } = {}) {
  const [online, setOnline] = useState(() => readOnline(currentNavigator()));
  const onReconnectRef = useRef(onReconnect);
  onReconnectRef.current = onReconnect;
  // Coupure observée depuis le dernier retour du réseau (page ouverte hors ligne comprise)
  const wasOfflineRef = useRef(!online);

  useEffect(() => {
    const goOffline = () => {
      wasOfflineRef.current = true;
      setOnline(false);
    };
    const goOnline = () => {
      setOnline(true);
      if (!wasOfflineRef.current) return;
      wasOfflineRef.current = false;
      onReconnectRef.current?.();
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    // Changement survenu entre le premier rendu et l'abonnement
    if (!readOnline(currentNavigator())) goOffline();
    else if (wasOfflineRef.current) goOnline();
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  return online;
}
